"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";

const cormorant = {
    className: "font-cormorant font-serif",
};

export interface LifecycleStep {
    title: string;
    subtitle: string;
    description: string;
    image: string;
}

export interface GuestiqoLifecycleBlockProps {
    tag?: string;
    title?: string;
    subtitle?: string;
    steps?: LifecycleStep[];
}

export default function GuestiqoLifecycleBlock({
    tag = "// Visual Narrative",
    title = "The Stay Lifecycle",
    subtitle = "Click on any lifecycle stage to preview the guest flow and visual layout interfaces.",
    steps = [
        {
            title: "01 / Digital Guidebook",
            subtitle: "Instant entry & property guide",
            description: "Guests scan a physical QR code to instantly load Wi-Fi credentials, check-in details, house guidelines, and appliance manuals in under 1.5 seconds. Zero app downloads, zero friction, and custom branding.",
            image: "/images/guestiqo_demo_home.png"
        },
        {
            title: "02 / Local Discovery",
            subtitle: "Curated experiences",
            description: "Host-recommended cafes, pubs, restaurants, hiking routes, and local attractions are mapped directly on an interactive guide page, bypassing generic searches with authentic local curations.",
            image: "/images/guestiqo_demo_experiences.png"
        },
        {
            title: "03 / Memory Wall",
            subtitle: "Emotional connection",
            description: "Guests share photos and notes of their stay to the digital guestbook ledger. Future visitors see these past experiences, building a warm communal sentiment and repeat-booking trust.",
            image: "/images/guestiqo_demo_memories.png"
        }
    ]
}: GuestiqoLifecycleBlockProps) {
    const [activeStep, setActiveStep] = useState(0);

    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#FDFBF7] w-full" data-header-theme="dark">
            <div className="container mx-auto">
                <div className="max-w-2xl mb-16">
                    {tag && <span className="text-xs font-mono text-[#8C8276] uppercase tracking-widest block mb-4">{tag}</span>}
                    {title && <h2 className={`${cormorant.className} text-4xl md:text-6xl font-light text-[#1C1C1C] leading-tight`}>{title}</h2>}
                    {subtitle && <p className="text-[#8C8276] text-sm mt-2 font-sans font-light">{subtitle}</p>}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
                    {/* Left Selector List */}
                    <div className="lg:col-span-5 flex flex-col justify-center space-y-4">
                        {steps.map((step, idx) => {
                            const isSelected = idx === activeStep;
                            return (
                                <button
                                    key={idx}
                                    onClick={() => setActiveStep(idx)}
                                    className={`w-full text-left p-6 border transition-all duration-500 flex flex-col rounded-xl relative overflow-hidden group ${
                                        isSelected 
                                            ? "bg-[#F7F3EB] border-[#1C1C1C]/20 shadow-xs" 
                                            : "bg-transparent border-[#1C1C1C]/5 hover:border-[#1C1C1C]/15"
                                    }`}
                                >
                                    <div className="flex justify-between items-center w-full">
                                        <span className="text-[11px] font-mono text-[#8C8276] uppercase tracking-wider">{step.title}</span>
                                        {isSelected && (
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#1C1C1C]" />
                                        )}
                                    </div>
                                    <h3 className={`${cormorant.className} text-2xl font-medium text-[#1C1C1C] mt-2 mb-1`}>
                                        {step.subtitle}
                                    </h3>
                                    <AnimatePresence initial={false}>
                                        {isSelected && (
                                            <motion.p 
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: "auto" }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ duration: 0.3 }}
                                                className="text-xs text-[#5E5E5E] leading-relaxed mt-2 font-sans font-light"
                                            >
                                                {step.description}
                                            </motion.p>
                                        )}
                                    </AnimatePresence>
                                </button>
                            );
                        })}
                    </div>

                    {/* Right Showcase Frame */}
                    <div className="lg:col-span-7 flex items-center justify-center bg-[#FAF7F2] border border-[#1C1C1C]/5 rounded-2xl p-8 lg:p-12 relative overflow-hidden min-h-[500px] lg:min-h-[680px]">
                        {/* Ambient Glow */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#8C8276]/5 rounded-full blur-3xl pointer-events-none" />

                        <div className="relative w-full h-full flex items-center justify-center">
                            <AnimatePresence mode="wait">
                                {steps[activeStep] && (
                                    <motion.div
                                        key={activeStep}
                                        initial={{ opacity: 0, scale: 0.97, y: 12 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.97, y: -12 }}
                                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                                        className="relative w-full max-w-[285px] aspect-[9/19.5] rounded-[3.2rem] p-[4px] bg-gradient-to-b from-[#D4CBB5] via-[#A89E88] to-[#5C5443] shadow-[0_25px_60px_-15px_rgba(43,40,35,0.12),0_0_40px_rgba(43,40,35,0.04)] ring-1 ring-[#A89E88]/30"
                                    >
                                        {/* Physical Buttons - Left Side: Ultra-slim volume keys */}
                                        <div className="absolute left-[-1.5px] top-24 w-[1.5px] h-6 bg-[#A89E88] rounded-l-xs opacity-75" />
                                        <div className="absolute left-[-1.5px] top-34 w-[1.5px] h-10 bg-[#A89E88] rounded-l-xs opacity-75" />
                                        <div className="absolute left-[-1.5px] top-47 w-[1.5px] h-10 bg-[#A89E88] rounded-l-xs opacity-75" />
                                        
                                        {/* Physical Buttons - Right Side: Ultra-slim power key */}
                                        <div className="absolute right-[-1.5px] top-38 w-[1.5px] h-14 bg-[#A89E88] rounded-r-xs opacity-75" />

                                        {/* Inner Bezel Frame: iPhone 17 Pro style ultra-narrow bezel */}
                                        <div className="relative w-full h-full rounded-[3.0rem] bg-[#0A0A0A] p-[2.5px] ring-1 ring-white/5">
                                            
                                            {/* Screen Reflective Glare (Translucent glass sheen) */}
                                            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.01] to-white/[0.06] pointer-events-none z-25 rounded-[2.8rem]" />

                                            {/* Minimal Screen Edge Shine */}
                                            <div className="absolute inset-0 border border-white/5 rounded-[2.8rem] pointer-events-none z-25" />

                                            {/* Home Indicator Bar */}
                                            <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 w-24 h-[3px] bg-white/20 rounded-full z-30 pointer-events-none" />

                                            {/* Screenshot Display Frame - Full edge-to-edge presentation */}
                                            <div className="relative w-full h-full overflow-hidden rounded-[2.8rem] bg-[#FAF7F2]">
                                                <Image
                                                    src={steps[activeStep].image}
                                                    alt={steps[activeStep].title}
                                                    fill
                                                    className="object-cover object-top"
                                                    sizes="(max-w-md) 100vw, 300px"
                                                    priority
                                                />
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
